import { c as _c } from "react/compiler-runtime";
import { useAppearance } from '@onefootprint/appearance';
import { getSessionId } from '@onefootprint/dev-tools';
import { hostedOnboardingD2pGenerateOptions } from '@onefootprint/fetch-hosted';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';
import useD2pStatusPoll, { isTerminalStatus } from '@/idv2/hooks/use-d2p-status-poll';
import { type DeviceInfo, isDesktopDevice } from '@/idv2/utils/get-device';
import { getLogger } from '@/idv2/utils/logger';
const {
  logInfo,
  logWarn
} = getLogger({
  name: 'idv2',
  location: 'use-d2p-session'
});
export { isTerminalStatus };
type UseD2pSessionParams = {
  authToken: string;
  device: DeviceInfo;
  l10n?: {
    language?: string;
    locale?: string;
  };
  onCancel?: () => void;
  onFinish?: () => void;
  sandboxIdDocOutcome?: string;
};

// Manages a D2P session: generating scoped tokens, polling for status updates, and canceling sessions
const useD2pSession = t0 => {
  const $ = _c(32);
  const {
    authToken,
    device,
    l10n,
    onCancel,
    onFinish,
    sandboxIdDocOutcome
  } = t0;
  const queryClient = useQueryClient();
  const appearance = useAppearance();
  const styleParams = appearance ? JSON.stringify(appearance) : undefined;
  const sessionId = getSessionId();
  let t1;
  if ($[0] !== device) {
    t1 = isDesktopDevice(device);
    $[0] = device;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const isMobile = !t1;
  let t2;
  if ($[2] !== isMobile) {
    t2 = () => isMobile ? window.location.href : undefined;
    $[2] = isMobile;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const [redirectUrl] = useState(t2);
  const generateQueryOptions = hostedOnboardingD2pGenerateOptions({
    headers: {
      "X-Fp-Authorization": authToken
    },
    body: {
      meta: {
        opener: device.type,
        sessionId: sessionId ?? undefined,
        styleParams,
        sandboxIdDocOutcome,
        redirectUrl,
        l10n: l10n ? {
          language: l10n.language,
          locale: l10n.locale ?? "en-US"
        } : undefined
      }
    }
  });
  let t3;
  if ($[4] !== generateQueryOptions) {
    t3 = {
      ...generateQueryOptions,
      gcTime: 0,
      refetchOnWindowFocus: false
    };
    $[4] = generateQueryOptions;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const generateQuery = useQuery(t3);
  const scopedAuthToken = generateQuery.data?.authToken;
  const t4 = scopedAuthToken ?? "";
  const t5 = !!scopedAuthToken;
  let t6;
  if ($[6] !== onCancel || $[7] !== onFinish) {
    t6 = status => {
      if (status === "completed" || status === "failed") {
        logInfo(`D2P session ${status}`);
        onFinish?.();
      } else {
        if (status === "canceled") {
          logInfo("D2P session canceled");
          onCancel?.();
        }
      }
    };
    $[6] = onCancel;
    $[7] = onFinish;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== t4 || $[10] !== t5 || $[11] !== t6) {
    t7 = {
      authToken: t4,
      enabled: t5,
      onStatusChange: t6
    };
    $[9] = t4;
    $[10] = t5;
    $[11] = t6;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  const d2pPoll = useD2pStatusPoll(t7);
  let t8;
  if ($[13] !== generateQuery || $[14] !== generateQueryOptions.queryKey || $[15] !== queryClient) {
    t8 = async () => {
      queryClient.removeQueries({
        queryKey: generateQueryOptions.queryKey
      });
      await generateQuery.refetch();
    };
    $[13] = generateQuery;
    $[14] = generateQueryOptions.queryKey;
    $[15] = queryClient;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  const regenerate = t8;
  const handledPollError = useRef(null);
  let t10;
  let t9;
  if ($[17] !== d2pPoll.error || $[18] !== regenerate) {
    t9 = () => {
      if (!d2pPoll.error) {
        handledPollError.current = null;
        return;
      }
      if (handledPollError.current === d2pPoll.error) {
        return;
      }
      handledPollError.current = d2pPoll.error;
      logWarn("D2P session polling error, likely expired. Regenerating token.", d2pPoll.error);
      regenerate();
    };
    t10 = [d2pPoll.error, regenerate];
    $[17] = d2pPoll.error;
    $[18] = regenerate;
    $[19] = t10;
    $[20] = t9;
  } else {
    t10 = $[19];
    t9 = $[20];
  }
  useEffect(t9, t10);
  const t11 = generateQuery.error ?? d2pPoll.error;
  let t12;
  if ($[21] !== d2pPoll.isCanceling || $[22] !== d2pPoll.isPolling || $[23] !== d2pPoll.status || $[24] !== generateQuery.isPending || $[25] !== scopedAuthToken || $[26] !== t11) {
    t12 = {
      scopedAuthToken,
      status: d2pPoll.status,
      isGenerating: generateQuery.isPending,
      isCanceling: d2pPoll.isCanceling,
      isPolling: d2pPoll.isPolling,
      error: t11
    };
    $[21] = d2pPoll.isCanceling;
    $[22] = d2pPoll.isPolling;
    $[23] = d2pPoll.status;
    $[24] = generateQuery.isPending;
    $[25] = scopedAuthToken;
    $[26] = t11;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  let t13;
  if ($[28] !== d2pPoll.cancel || $[29] !== regenerate || $[30] !== t12) {
    t13 = {
      state: t12,
      cancel: d2pPoll.cancel,
      regenerate
    };
    $[28] = d2pPoll.cancel;
    $[29] = regenerate;
    $[30] = t12;
    $[31] = t13;
  } else {
    t13 = $[31];
  }
  return t13;
};
export default useD2pSession;